/**
 * The guest checkout payment-widget A/B test: which widget a guest sees, the
 * switch on the Orders dash that runs the test, and what it has measured.
 *
 * `styled` is the Sellwild-themed tab form guest checkout has used since
 * 2026-09-12 (b7a3b52ab), card only. `classic` is what it replaced: Stripe's
 * stock Payment Element with no appearance and no options, offering whatever
 * the Stripe Dashboard has enabled (Google Pay, Cash App Pay, Link, card).
 */

import { z } from 'zod';

export const CheckoutWidgetVariantSchema = z.enum(['styled', 'classic']);
export type CheckoutWidgetVariant = z.infer<typeof CheckoutWidgetVariantSchema>;

export const CheckoutWidgetAbBodySchema = z.object({
  enabled: z.boolean(),
});
export type CheckoutWidgetAbBody = z.infer<typeof CheckoutWidgetAbBodySchema>;

/** One arm of the test, over the window asked for. */
export const CheckoutWidgetArmSchema = z.object({
  variant: CheckoutWidgetVariantSchema,
  /** Distinct guests who were shown this widget at the payment step. */
  reachedPayment: z.number(),
  /** Of those, how many pressed Pay at least once. */
  pressedPay: z.number(),
  /** Of those, how many have an order (a LISTING_TRANSACTION for their intent). */
  paid: z.number(),
  /** paid / reachedPayment, 0..1. Null with nobody in the arm. */
  conversion: z.number().nullable(),
});
export type CheckoutWidgetArm = z.infer<typeof CheckoutWidgetArmSchema>;

export const CheckoutWidgetAbStateSchema = z.object({
  /** True while guests are being split 50/50. Off means everyone gets `styled`. */
  enabled: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /** Start of the window the arms were counted over (ISO). */
  since: z.string(),
  arms: z.array(CheckoutWidgetArmSchema),
  /**
   * Two-sided p-value for "the two arms convert at the same rate". Null until
   * both arms have someone in them. Below 0.05 is the usual bar for a real gap.
   */
  pValue: z.number().nullable(),
});
export type CheckoutWidgetAbState = z.infer<typeof CheckoutWidgetAbStateSchema>;
